import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, setCsrf, ApiError } from './api';

export interface AuthState {
  authed: boolean;
  loading: boolean;
  refresh: () => void;
  logout: () => Promise<void>;
}

// Probe the session AND re-sync the CSRF token: the session cookie is durable but the
// CSRF token lives in sessionStorage (lost on a fresh PWA launch), so we re-fetch it here.
async function probe(): Promise<boolean> {
  try {
    const { csrf_token } = await api.get<{ csrf_token: string }>('/auth/csrf');
    setCsrf(csrf_token);
    return true;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      setCsrf(null);
      return false;
    }
    throw e;
  }
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['auth'], queryFn: probe, retry: false, staleTime: 30_000 });

  const value: AuthState = {
    authed: q.data === true,
    loading: q.isPending,
    refresh: () => {
      void qc.invalidateQueries({ queryKey: ['auth'] });
    },
    logout: async () => {
      try {
        await api.post('/auth/logout', {});
      } finally {
        setCsrf(null);
        void qc.invalidateQueries();
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used within <AuthProvider>');
  return v;
}
